import { Button, Group, TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowRight, IconFileText, IconFolder } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DirectorySelectionModal } from 'src/Components/Modals/DirectorySelectionModal';
import { useTranslateFolderName } from 'src/Hooks/Format/useTranslateFolderName';

import styles from './DocumentSuggestionCard.module.scss';
import { Props } from './DocumentSuggestionCard.types';

const splitExtension = (fileName: string) => {
  const index = fileName.lastIndexOf('.');
  return index > 0
    ? { base: fileName.slice(0, index), extension: fileName.slice(index) }
    : { base: fileName, extension: '' };
};

const DocumentSuggestionCard = ({ suggestion, onAccept, onReject, loading }: Props) => {
  const { t } = useTranslation();
  const translateFolderName = useTranslateFolderName();
  const [editing, setEditing] = useState(false);
  const [folderModalOpen, { open: openFolderModal, close: closeFolderModal }] = useDisclosure();

  const proposedName = suggestion.proposedName ?? suggestion.originalName;
  const proposedPath = suggestion.proposedPath ?? suggestion.originalPath;
  const { extension } = splitExtension(proposedName);
  const [nameBase, setNameBase] = useState(splitExtension(proposedName).base);
  const [path, setPath] = useState(proposedPath);
  const [nameError, setNameError] = useState<string>();

  const isRenamed = proposedName !== suggestion.originalName;

  const formatPath = (directoryPath: string) => {
    const segments = directoryPath.split('/').filter(Boolean);
    return segments.length > 0
      ? segments.map(translateFolderName).join(' › ')
      : t('documents.suggestions.rootFolder', 'Documents (racine)');
  };

  const handleSaveEdit = () => {
    const trimmed = nameBase.trim();
    if (!trimmed) {
      setNameError(t('documents.suggestions.nameRequired', 'Le nom ne peut pas être vide'));
      return;
    }
    if (/[/\\]/.test(trimmed)) {
      setNameError(
        t('documents.suggestions.nameInvalid', 'Le nom ne peut pas contenir « / » ni « \\ »')
      );
      return;
    }
    onAccept({ name: `${trimmed}${extension}`, path });
  };

  const handleCancelEdit = () => {
    setNameBase(splitExtension(proposedName).base);
    setPath(proposedPath);
    setNameError(undefined);
    setEditing(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <IconFileText size={20} className={styles.icon} />
        <span className={styles.originalName}>{suggestion.originalName}</span>
        {suggestion.documentTypeLabel && (
          <span className={styles.type}>{suggestion.documentTypeLabel}</span>
        )}
      </div>

      {editing ? (
        <div className={styles.edit}>
          <TextInput
            label={t('documents.suggestions.newName', 'Nouveau nom')}
            value={nameBase}
            onChange={event => {
              setNameBase(event.currentTarget.value);
              setNameError(undefined);
            }}
            rightSection={extension ? <span className={styles.extension}>{extension}</span> : null}
            rightSectionWidth={extension ? 56 : undefined}
            error={nameError}
          />
          <div className={styles.line}>
            <IconFolder size={18} className={styles.icon} />
            <span>{formatPath(path)}</span>
            <Button variant="outline" size="sm" onClick={openFolderModal}>
              {t('documents.suggestions.changeFolder', 'Changer de dossier')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.proposal}>
          {isRenamed && (
            <div className={styles.line}>
              <IconArrowRight size={18} className={styles.icon} />
              <span className={styles.proposedName}>{proposedName}</span>
            </div>
          )}
          <div className={styles.line}>
            <IconFolder size={18} className={styles.icon} />
            <span>
              {proposedPath === suggestion.originalPath
                ? t('documents.suggestions.staysIn', 'Reste dans {{folder}}', {
                    folder: formatPath(proposedPath),
                  })
                : t('documents.suggestions.moveTo', 'Ranger dans {{folder}}', {
                    folder: formatPath(proposedPath),
                  })}
            </span>
          </div>
        </div>
      )}

      <Group gap="sm" className={styles.actions}>
        {editing ? (
          <>
            <Button size="md" onClick={handleSaveEdit} loading={loading}>
              {t('documents.suggestions.saveAndFile', 'Valider')}
            </Button>
            <Button size="md" variant="outline" onClick={handleCancelEdit} disabled={loading}>
              {t('documents.suggestions.cancelEdit', 'Annuler')}
            </Button>
          </>
        ) : (
          <>
            <Button size="md" onClick={() => onAccept({})} loading={loading}>
              {t('documents.suggestions.accept', 'Accepter')}
            </Button>
            <Button size="md" variant="outline" onClick={() => setEditing(true)} disabled={loading}>
              {t('documents.suggestions.edit', 'Modifier')}
            </Button>
            <Button size="md" variant="outline" color="gray" onClick={onReject} disabled={loading}>
              {t('documents.suggestions.reject', 'Ignorer')}
            </Button>
          </>
        )}
      </Group>

      <DirectorySelectionModal
        open={folderModalOpen}
        onClose={closeFolderModal}
        title={t('documents.suggestions.folderModalTitle', 'Choisir un dossier')}
        subtitle={t('documents.move.subtitle', 'Choisissez le dossier de destination')}
        buttonText={t('documents.suggestions.folderModalButton', 'Choisir ce dossier')}
        onValidate={value => {
          setPath(value.path);
          closeFolderModal();
        }}
      />
    </div>
  );
};

export default DocumentSuggestionCard;
